import React, { useState, useEffect } from 'react';
import { Sparkles, Bot, CheckCircle2, TrendingUp, Star, Clock, Target, FileText, Award, Users, Zap, Layout, Search, BarChart, Briefcase, ArrowRight } from 'lucide-react';
import Spline from '@splinetool/react-spline';

interface LandingProps {
  onAuthClick: () => void;
}

export function Landing({ onAuthClick }: LandingProps) {
  // Añadir detector de tamaño de pantalla
  const [isMobile, setIsMobile] = useState(false);

  // Efecto para detectar el tamaño de pantalla y ajustar el comportamiento
  useEffect(() => {
    // Función para actualizar el estado según el ancho de la ventana
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Ejecutar al inicio
    handleResize();

    // Agregar listener para cambios de tamaño
    window.addEventListener('resize', handleResize);

    // Limpiar listener al desmontar
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Calcular el tamaño y la velocidad del carrusel basado en el tamaño de pantalla
  const carouselSpeed = isMobile ? 25 : 50; // Más lento y suave en móvil (25s vs 50s)
  const cardSize = isMobile ? 'min-w-[120px]' : 'min-w-[380px]';
  const cardPadding = isMobile ? 'p-1' : 'p-4';
  const containerPadding = isMobile ? 'py-3' : 'py-6';

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section */}
      <section className="relative w-full min-h-[90vh] flex items-center overflow-hidden">
        {/* Fondos */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-black" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1604933762021-54a5858c9832?ixlib=rb-4.0.3')] bg-cover bg-center opacity-5" />

        {/* Efecto de resplandor */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-800/20 rounded-full blur-[100px] animate-pulse" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-blue-800/20 rounded-full blur-[80px] animate-pulse delay-300" />
        </div>

        {/* Contenido */}
        <div className="w-full relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Grid de contenido */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="mt-0">
                {/* Badge POTENCIADO POR IA primero (arriba del título) */}
                <div className="flex justify-center md:justify-start ml-0 mb-6">
                  <div className="relative">
                    {/* Efecto de resplandor animado */}
                    <div className="absolute -inset-1.5 bg-gradient-to-r from-cyan-500/40 via-blue-500/40 to-purple-500/40 rounded-lg blur-md animate-pulse-slow"></div>
                    <div className="absolute -inset-3 bg-gradient-to-r from-blue-600/10 via-cyan-400/10 to-purple-600/10 rounded-xl blur-lg animate-glow-slow"></div>
                    <div className="relative inline-flex items-center px-5 py-2.5 bg-[#161b3c] rounded-lg shadow-lg z-10 border border-cyan-500/30">
                      <Sparkles className="w-4 h-4 text-cyan-300 mr-2 animate-twinkle" />
                      <span className="text-sm font-mono tracking-wider text-white">
                        {`POTENCIADO POR IA`}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Título principal ahora después del badge - CENTRADO */}
                <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight text-center">
                  Crea tu CV en{' '}
                  <span className="relative">
                    <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-300 animate-pulse">
                      Segundos
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-300/20 blur-lg animate-pulse" />
                  </span>
                  {' '}
                  <span className="mx-2">con</span>
                  {' '}
                  <span className="relative">
                    <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-300 animate-pulse">
                      IA
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-300/20 blur-lg animate-pulse" />
                  </span>
                </h1>

                {/* Imagen CV para móvil - debajo del título, 40% más pequeña */}
                <div className="block lg:hidden mb-6">
                  <div className="relative group max-w-[200px] mx-auto">
                    <div className="absolute inset-0 bg-purple-500/20 rounded-lg blur-md opacity-0 group-hover:opacity-100 transition-opacity" />

                    <img
                      src="/design/cvmaker_01.jpg"
                      alt="CV Template Preview"
                      className="relative z-10 w-full transform rotate-1 rounded-lg shadow-xl hover:rotate-0 transition-all duration-500"
                    />
                  </div>
                </div>

                <p className="text-lg text-gray-300 mb-6">
                  Nuestra IA analiza tu CV actual y crea uno impactante que destaca entre la competencia en menos de 10 segundos.
                </p>

                {/* Info badges más compactos y en línea */}
                <div className="flex flex-wrap justify-center gap-3 mb-6">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10 hover:border-purple-500/50 transition-all group">
                    <CheckCircle2 className="w-4 h-4 text-green-400 group-hover:scale-110 transition-transform" />
                    <span className="text-sm text-gray-200">7x más probabilidades</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10 hover:border-purple-500/50 transition-all group">
                    <Clock className="w-4 h-4 text-purple-400 group-hover:scale-110 transition-transform" />
                    <span className="text-sm text-gray-200">menos de 10 segundos</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10 hover:border-purple-500/50 transition-all group">
                    <Star className="w-4 h-4 text-yellow-400 group-hover:scale-110 transition-transform" />
                    <span className="text-sm text-gray-200">Diseños probados</span>
                  </div>
                </div>

                {/* Botón centrado */}
                <div className="flex justify-center">
                  <button
                    onClick={onAuthClick}
                    className="group relative px-8 py-4 bg-gradient-to-r from-purple-800 to-purple-600 text-white rounded-lg transition-all transform hover:scale-105 hover:shadow-[0_0_30px_rgba(147,51,234,0.7)]"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      <span>Crear con IA</span>
                      <Sparkles className="w-5 h-5 animate-pulse text-yellow-200" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-800 to-purple-600 rounded-lg blur opacity-0 group-hover:opacity-50 transition-opacity" />
                  </button>
                </div>
              </div>

              {/* Imagen CV para desktop */}
              <div className="hidden lg:block">
                <div className="relative group">
                  <div className="absolute inset-0 bg-purple-500/30 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />

                  <img
                    src="/design/cvmaker_01.jpg"
                    alt="CV Template Preview"
                    className="relative z-10 w-full max-w-md mx-auto transform rotate-3 rounded-lg shadow-2xl hover:rotate-0 transition-all duration-500"
                  />
                </div>
              </div>


            </div>
          </div>
        </div>
      </section>

      {/* Template Showcase - Carrusel Automatico DESPUÉS DEL HERO */}
      <section className="w-full py-24 relative overflow-hidden bg-gradient-to-br from-blue-950 to-purple-950">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] bg-purple-500/10 rounded-full blur-[80px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm rounded-full text-blue-400 mb-6 border border-blue-500/30 animate-pulse">
              <Sparkles className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Diseños Exclusivos</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Múltiples plantillas <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">profesionales optimizadas con IA</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Todos los diseños son completamente personalizables según tu perfil y la industria de tu interés
            </p>
          </div>

          {/* Carrusel de plantillas con efecto película - Versión mejorada y responsive */}
          <div className="relative mx-auto max-w-full md:max-w-5xl">
            {/* Efectos de resplandor en los bordes */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
            <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"></div>
            <div className="absolute top-0 left-0 h-full w-[2px] bg-gradient-to-b from-transparent via-blue-500/50 to-transparent"></div>
            <div className="absolute top-0 right-0 h-full w-[2px] bg-gradient-to-b from-transparent via-purple-500/50 to-transparent"></div>

            {/* Esquinas con resplandor */}
            <div className="absolute top-0 left-0 w-8 md:w-12 h-8 md:h-12 border-t-2 border-l-2 border-blue-500/50 rounded-tl-2xl"></div>
            <div className="absolute top-0 right-0 w-8 md:w-12 h-8 md:h-12 border-t-2 border-r-2 border-blue-500/50 rounded-tr-2xl"></div>
            <div className="absolute bottom-0 left-0 w-8 md:w-12 h-8 md:h-12 border-b-2 border-l-2 border-purple-500/50 rounded-bl-2xl"></div>
            <div className="absolute bottom-0 right-0 w-8 md:w-12 h-8 md:h-12 border-b-2 border-r-2 border-purple-500/50 rounded-br-2xl"></div>

            {/* Efecto de degradado en los bordes */}
            <div className="absolute top-0 left-0 w-full h-12 md:h-16 bg-gradient-to-b from-blue-950 to-transparent z-10 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-full h-12 md:h-16 bg-gradient-to-t from-blue-950 to-transparent z-10 pointer-events-none"></div>
            <div className="absolute top-0 left-0 h-full w-12 md:w-16 bg-gradient-to-r from-blue-950 to-transparent z-10 pointer-events-none"></div>
            <div className="absolute top-0 right-0 h-full w-12 md:w-16 bg-gradient-to-l from-blue-950 to-transparent z-10 pointer-events-none"></div>

            {/* Contenedor con overflow para el carrusel con bordes redondeados y efecto de cristal */}
            <div className={`overflow-hidden rounded-3xl bg-blue-950/30 backdrop-blur-sm border border-blue-500/20 shadow-[0_0_25px_rgba(37,99,235,0.2)] ${isMobile ? 'h-[380px]' : ''}`}>
              {/* Carrusel con animación continua - aplicando propiedades dinámicas */}
              <div className={`flex animate-carousel ${isMobile ? 'py-4' : 'py-6'}`} style={{
                animationDuration: `${carouselSpeed}s`,
                transform: isMobile ? 'scale(0.8)' : 'scale(1)'
              }}>
                {/* Primera copia de las imágenes para el bucle - TODOS los 8 CVs */}
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={`first-${i}`} className={`${isMobile ? 'min-w-[280px] sm:min-w-[320px]' : 'min-w-[380px]'} ${isMobile ? 'p-2' : 'p-4'} flex-shrink-0 transition-all`}>
                    <div className={`group relative bg-gradient-to-br from-blue-900/40 to-purple-900/40 ${isMobile ? 'p-2' : 'p-3'} rounded-2xl border border-blue-500/30 shadow-md hover:border-purple-500/50 transition-all overflow-hidden`}>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                      {/* Efecto de brillo en la esquina - más sutil en móvil */}
                      <div className={`absolute -top-5 -right-5 ${isMobile ? 'w-10 h-10' : 'w-20 h-20'} bg-blue-400/30 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                      <img
                        src={`/design/cvmaker_0${i}.jpg`}
                        alt={`Plantilla de CV ${i}`}
                        className={`relative z-10 w-full rounded-xl shadow-lg transform transition-transform duration-500 group-hover:scale-[1.02] ${isMobile ? 'max-h-[340px] object-cover' : ''}`}
                        // Añadir loading lazy para mejorar rendimiento
                        loading="lazy"
                      />
                    </div>
                  </div>
                ))}

                {/* Segunda copia para crear la ilusión de desplazamiento infinito - TODOS los 8 CVs */}
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={`second-${i}`} className={`${isMobile ? 'min-w-[280px] sm:min-w-[320px]' : 'min-w-[380px]'} ${isMobile ? 'p-2' : 'p-4'} flex-shrink-0 transition-all`}>
                    <div className={`group relative bg-gradient-to-br from-blue-900/40 to-purple-900/40 ${isMobile ? 'p-2' : 'p-3'} rounded-2xl border border-blue-500/30 shadow-md hover:border-purple-500/50 transition-all overflow-hidden`}>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                      {/* Efecto de brillo en la esquina - más sutil en móvil */}
                      <div className={`absolute -top-5 -right-5 ${isMobile ? 'w-10 h-10' : 'w-20 h-20'} bg-blue-400/30 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                      <img
                        src={`/design/cvmaker_0${i}.jpg`}
                        alt={`Plantilla de CV ${i}`}
                        className={`relative z-10 w-full rounded-xl shadow-lg transform transition-transform duration-500 group-hover:scale-[1.02] ${isMobile ? 'max-h-[340px] object-cover' : ''}`}
                        loading="lazy"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <style jsx global>{`
        @keyframes carousel {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-100%);
          }
        }
        
        .animate-carousel {
          animation: carousel linear infinite;
        }
        
        .animate-carousel:hover {
          animation-play-state: paused;
        }

        /* Añadir estilos de escalado para dispositivos realmente pequeños */
        @media (max-width: 360px) {
          .animate-carousel {
            transform: scale(0.85);
            transform-origin: center center;
          }
        }
      `}</style>

      {/* Agregar estilos para el recorte diagonal */}
      <style>{`
        .clip-polygon-diagonal-1 {
          clip-path: polygon(0 0, 100% 0, 25% 100%, 0 100%);
        }
        .clip-polygon-diagonal-2 {
          clip-path: polygon(25% 100%, 100% 0, 100% 100%, 0 100%);
        }
        
        @keyframes spin-slow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 15s linear infinite;
        }
        
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 1; }
          75%, 100% { transform: scale(1.5); opacity: 0; }
        }
        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s infinite;
        }
      `}</style>



      {/* Transform CV Section - DESPUÉS DEL TEMPLATE SHOWCASE */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-br from-gray-950 via-purple-950/10 to-gray-900">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-purple-900/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-blue-900/15 rounded-full blur-[80px]" />
          <div className="absolute top-2/3 right-1/3 w-[250px] h-[250px] bg-purple-900/10 rounded-full blur-[70px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Consigue ese empleo anhelado con <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-300 animate-pulse">Curvia AI</span>
            </h2>
            <p className="text-xl text-gray-300">
              Aumenta hasta <span className="text-purple-400 font-semibold">7 veces</span> tus probabilidades con diseños optimizados por IA que superan los filtros automáticos
            </p>
          </div>

          {/* Visualización de antes y después con imágenes reales de CVs */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-12">
            {/* Vista móvil especial - solo visible en móviles */}
            <div className="block md:hidden w-full mb-8">
              <div className="relative">
                {/* CV Antiguo */}
                <div className="mb-6">
                  <div className="inline-block px-4 py-1.5 mb-3 bg-red-900/80 text-red-100 rounded-full text-sm font-medium backdrop-blur-sm border border-red-900/50 shadow-lg">
                    Antes
                  </div>
                  <div className="group relative bg-gray-900/60 backdrop-blur-sm p-3 rounded-xl border border-red-500/30 shadow-lg">
                    <img
                      src="/design/cvmaker_antes.jpg"
                      alt="CV Tradicional"
                      className="rounded-lg w-full shadow-lg max-h-[400px] object-contain"
                    />
                  </div>
                </div>

                {/* Flecha animada en el medio - HACIA ABAJO en móvil - MÁS SUTIL */}
                <div className="flex justify-center items-center my-4">
                  <div className="relative w-12 h-12 flex items-center justify-center">
                    {/* Círculo sutil */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/30 to-blue-500/30 rounded-full shadow-md"></div>

                    {/* Flecha sutil - HACIA ABAJO en móvil */}
                    <svg className="w-6 h-6 text-white relative z-10 animate-bounce" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 5v14M12 19l-4-4M12 19l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>

                {/* CV Nuevo */}
                <div className="mb-8">
                  <div className="inline-block px-4 py-1.5 mb-3 bg-green-900/80 text-green-100 rounded-full text-sm font-medium backdrop-blur-sm border border-green-900/50 shadow-lg">
                    Después con <span className="font-bold">Curvia AI</span>
                  </div>
                  <div className="group relative bg-gray-900/60 backdrop-blur-sm p-3 rounded-xl border border-green-500/30 shadow-lg">
                    <img
                      src="/design/cvmaker_despues.jpg"
                      alt="CV Optimizado"
                      className="rounded-lg w-full shadow-lg max-h-[400px] object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Antes - CV tradicional - visible solo en pantallas medianas y grandes */}
            <div className="w-full md:w-5/12 flex flex-col items-center hidden md:flex">
              <div className="mb-4">
                <span className="inline-block px-6 py-2 bg-red-900/20 text-red-400 rounded-full text-lg font-medium backdrop-blur-sm border border-red-900/30">
                  Antes
                </span>
              </div>
              <div className="group relative bg-gray-900/60 backdrop-blur-sm p-3 rounded-xl border border-red-500/30 shadow-lg hover:border-red-500/50 transition-all">
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 to-red-900/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <img
                  src="/design/cvmaker_antes.jpg"
                  alt="CV Tradicional"
                  className="rounded-lg w-full shadow-lg transform transition-all duration-500 group-hover:scale-[1.02] max-h-[500px] object-contain"
                />
              </div>
            </div>

            {/* Flecha animada en el medio - visible solo en pantallas medianas y grandes */}
            <div className="hidden md:flex justify-center items-center mx-1">
              <div className="relative w-36 h-20 flex items-center justify-center">
                {/* Círculos concéntricos animados */}
                <div className="absolute inset-[-15px] bg-gradient-to-r from-purple-600/30 to-blue-600/30 rounded-full blur-xl animate-pulse-slow"></div>
                <div className="absolute inset-[-10px] bg-gradient-to-tr from-violet-500/20 via-fuchsia-500/20 to-blue-500/20 rounded-full blur-lg animate-spin-slow"></div>
                <div className="absolute inset-[-5px] bg-gradient-to-br from-indigo-400/10 to-purple-500/10 rounded-full animate-ping-slow"></div>

                {/* Círculo principal con efecto de brillo */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/70 to-purple-700/70 rounded-full shadow-lg shadow-purple-600/50"></div>

                {/* Destellos */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-1 h-1 rounded-full bg-white shadow-[0_0_10px_5px_rgba(255,255,255,0.7)] animate-ping"></div>
                </div>

                {/* Flecha principal más larga */}
                <svg className="w-32 h-12 text-white relative z-10 drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" viewBox="0 0 48 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 12h40M44 12l-8-8M44 12l-8 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            {/* Después - CV optimizado - visible solo en pantallas medianas y grandes */}
            <div className="w-full md:w-5/12 flex flex-col items-center hidden md:flex">
              <div className="mb-4">
                <span className="inline-block px-6 py-2 bg-green-900/20 text-green-400 rounded-full text-lg font-medium backdrop-blur-sm border border-green-900/30">
                  Después con <span className="font-bold">Curvia AI</span>
                </span>
              </div>
              <div className="group relative bg-gray-900/60 backdrop-blur-sm p-3 rounded-xl border border-green-500/30 shadow-lg hover:border-green-500/50 transition-all">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-green-900/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <img
                  src="/design/cvmaker_despues.jpg"
                  alt="CV Optimizado"
                  className="rounded-lg w-full shadow-lg transform transition-all duration-500 group-hover:scale-[1.02] max-h-[500px] object-contain"
                />
              </div>
            </div>
          </div>

          {/* Tabla comparativa - visible solo en pantallas medianas y grandes */}
          <div className="hidden md:block mb-10">
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-purple-500/30 shadow-lg">
              <h3 className="text-center text-xl font-bold text-white mb-6">Comparativa: Tradicional vs Curvia AI</h3>

              <div className="grid grid-cols-2 gap-8">
                <div>
                  <h4 className="text-center font-bold text-red-400 text-lg pb-3 border-b border-gray-700 mb-4">CV Tradicional</h4>
                  <ul className="space-y-4">
                    <li className="flex items-start text-gray-300">
                      <span className="w-3 h-3 bg-red-400 rounded-full mt-1 mr-3 flex-shrink-0" />
                      <span>Formato básico y poco atractivo que no destaca entre otros candidatos</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <span className="w-3 h-3 bg-red-400 rounded-full mt-1 mr-3 flex-shrink-0" />
                      <span>Información desordenada que dificulta la lectura por reclutadores</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <span className="w-3 h-3 bg-red-400 rounded-full mt-1 mr-3 flex-shrink-0" />
                      <span>No optimizado para sistemas de seguimiento de aplicaciones (ATS)</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <span className="w-3 h-3 bg-red-400 rounded-full mt-1 mr-3 flex-shrink-0" />
                      <span>Logros descritos sin métricas ni impacto cuantificable en resultados</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-center font-bold text-green-400 text-lg pb-3 border-b border-gray-700 mb-4">CV con Curvia AI</h4>
                  <ul className="space-y-4">
                    <li className="flex items-start text-gray-300">
                      <span className="w-3 h-3 bg-green-400 rounded-full mt-1 mr-3 flex-shrink-0" />
                      <span>Diseño profesional con formato moderno que capta la atención inmediatamente</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <span className="w-3 h-3 bg-green-400 rounded-full mt-1 mr-3 flex-shrink-0" />
                      <span>Contenido perfectamente adaptado a cada puesto de trabajo específico</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <span className="w-3 h-3 bg-green-400 rounded-full mt-1 mr-3 flex-shrink-0" />
                      <span>Compatible con filtros ATS, maximizando probabilidades de superar la preselección</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <span className="w-3 h-3 bg-green-400 rounded-full mt-1 mr-3 flex-shrink-0" />
                      <span>Logros destacados con métricas precisas que demuestran impacto real</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 p-4 bg-purple-900/20 rounded-lg border border-purple-500/20">
                <p className="text-center text-purple-200">
                  <span className="font-bold">Curvia AI</span> genera tu CV optimizado en menos de 10 segundos, aumentando tus probabilidades de conseguir entrevistas hasta <span className="font-bold">7 veces</span> más que con métodos tradicionales
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section - Ahora después de Transform CV */}
      <section className="w-full py-20 relative overflow-hidden bg-gray-950">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-950">
          <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-purple-900/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] bg-purple-900/10 rounded-full blur-[80px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              ¿Por qué tu CV no consigue entrevistas?
            </h2>
            <p className="text-xl text-gray-400">
              Los problemas más comunes que impiden destacar en el mercado laboral
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group bg-gray-900/60 backdrop-blur-sm p-8 rounded-xl border border-gray-800/80 hover:border-red-900/50 transition-all shadow-lg">
              <Target className="w-12 h-12 text-red-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2">
                No supera los filtros ATS
              </h3>
              <p className="text-gray-400">
                75% de los CVs son rechazados por sistemas automatizados antes de llegar a un reclutador
              </p>
            </div>

            <div className="group bg-gray-900/60 backdrop-blur-sm p-8 rounded-xl border border-gray-800/80 hover:border-yellow-900/50 transition-all shadow-lg">
              <FileText className="w-12 h-12 text-yellow-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2">
                Formato inadecuado
              </h3>
              <p className="text-gray-400">
                Un diseño poco profesional reduce en 80% las posibilidades de ser considerado
              </p>
            </div>

            <div className="group bg-gray-900/60 backdrop-blur-sm p-8 rounded-xl border border-gray-800/80 hover:border-blue-900/50 transition-all shadow-lg">
              <Award className="w-12 h-12 text-blue-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2">
                Logros no destacados
              </h3>
              <p className="text-gray-400">
                90% de los CVs no comunican efectivamente los logros y el valor aportado
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Global Speed Section with World Animation */}
      <section className="w-full py-16 relative overflow-hidden bg-gray-950">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950 to-gray-900">
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-purple-900/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/3 right-1/3 w-[300px] h-[300px] bg-blue-900/10 rounded-full blur-[80px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-8">
            <div className="inline-flex items-center px-4 py-2 bg-purple-900/20 backdrop-blur-sm rounded-full text-purple-400 mb-3 border border-purple-900/30">
              <Clock className="w-4 h-4 mr-2 animate-pulse" />
              <span className="text-sm font-medium">Velocidad Incomparable</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Somos los más rápidos del Mundo
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Creamos tu CV con una rapidez que nos destaca de la competencia, en cosa de segundos tendrás tu CV automático optimizado con IA
            </p>
          </div>

          {/* Contenedor principal dividido en pestañas */}
          <div className="relative mt-16 bg-gray-900/60 rounded-xl border border-gray-800/80 shadow-lg overflow-hidden">
            {/* Contenido simplificado - Solo el mundo girando */}
            <div className="p-8">
              <div className="flex flex-col items-center justify-center">
                {/* Contenedor cuadrado para el planeta */}
                <div className="w-full max-w-xl aspect-square bg-black rounded-2xl overflow-hidden shadow-lg shadow-purple-900/20 border border-gray-800/80 mx-auto">
                  <div className="w-full h-full relative overflow-hidden" style={{ transform: 'scale(0.7)', transformOrigin: 'center center' }}>
                    <Spline scene="https://prod.spline.design/aWs7EtNPKUcvdHRC/scene.splinecode" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coins Section with Spline Animation */}
      <section className="w-full py-8 relative overflow-hidden bg-gradient-to-b from-gray-900 to-purple-950">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/3 right-1/3 w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[80px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-4">
            <div className="inline-flex items-center px-4 py-2 bg-yellow-500/20 backdrop-blur-sm rounded-full text-yellow-400 mb-3 border border-yellow-500/30">
              <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
              <span className="text-sm font-medium">Sistema de Monedas</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Monedas: Carga según tu uso
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Nuestro sistema inteligente de tokens te permite generar CVs optimizados de manera eficiente
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Spline Animation */}
            <div className="relative w-full md:w-1/2 lg:w-3/5 bg-gradient-to-br from-gray-800/50 to-purple-900/30 p-3 rounded-xl border border-purple-500/20 shadow-lg">
              <div className="w-full h-[380px] relative mx-auto">
                <Spline scene="https://prod.spline.design/iZSuQuj7nyCfLfzH/scene.splinecode" />
              </div>
            </div>

            {/* Benefits */}
            <div className="w-full md:w-1/2 lg:w-2/5 space-y-3">
              <div className="bg-white/5 backdrop-blur-sm p-4 rounded-lg border border-white/10 transition-all hover:border-yellow-500/30 group">
                <div className="flex items-start gap-3">
                  <div className="mt-1 w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center text-yellow-400 group-hover:scale-110 transition-transform">
                    <Zap className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Pago por uso</h3>
                    <p className="text-gray-300 text-sm">Solo pagas por los CVs que generas, sin suscripciones mensuales ni cargos ocultos</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm p-4 rounded-lg border border-white/10 transition-all hover:border-blue-500/30 group">
                <div className="flex items-start gap-3">
                  <div className="mt-1 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-400 group-hover:scale-110 transition-transform">
                    <Target className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">CV optimizado por puesto</h3>
                    <p className="text-gray-300 text-sm">Cada moneda te permite generar un CV completamente adaptado a la descripción del puesto</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm p-4 rounded-lg border border-white/10 transition-all hover:border-green-500/30 group">
                <div className="flex items-start gap-3">
                  <div className="mt-1 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 group-hover:scale-110 transition-transform">
                    <TrendingUp className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Paquetes con descuento</h3>
                    <p className="text-gray-300 text-sm">Ahorra adquiriendo paquetes de monedas con descuentos especiales para múltiples aplicaciones</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works - Versión mejorada */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-br from-purple-950 to-gray-900">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/3 right-1/3 w-[300px] h-[300px] bg-purple-500/10 rounded-full blur-[80px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm rounded-full text-blue-400 mb-6 border border-blue-500/30 animate-pulse">
              <Sparkles className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Proceso simplificado</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Optimiza tu CV en <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">3 simples pasos</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Nuestra IA hace todo el trabajo pesado por ti, tú solo necesitas seguir estos sencillos pasos
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <div className="relative group md:transform md:transition-all md:duration-500 md:hover:translate-y-[-10px]">
              {/* Círculo de número con efecto de resplandor */}
              <div className="absolute -top-6 -left-6 z-20">
                <div className="absolute inset-0 bg-blue-500/30 rounded-full blur-md animate-pulse"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-xl border-2 border-blue-400/30">
                  <span className="text-3xl font-bold text-white">1</span>
                </div>
              </div>

              {/* Tarjeta principal */}
              <div className="relative bg-gradient-to-br from-blue-900/40 to-indigo-900/40 backdrop-blur-sm p-8 pt-10 rounded-xl border border-blue-500/30 shadow-lg shadow-blue-900/20 overflow-hidden h-full">
                {/* Fondo glow */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-500/10 rounded-full blur-xl"></div>

                {/* Icono */}
                <div className="bg-blue-500/20 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <FileText className="w-8 h-8 text-blue-400" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">
                  Sube tu CV actual
                </h3>

                <p className="text-gray-300 mb-6 relative z-10">
                  Carga tu CV existente en PDF/Word o introduce tu experiencia manualmente en nuestro editor intuitivo.
                </p>

                {/* Lista detallada */}
                <ul className="space-y-3 text-gray-300 relative z-10">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Extracción automática de información relevante</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Reconocimiento inteligente de experiencia y habilidades</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="relative group md:transform md:transition-all md:duration-500 md:hover:translate-y-[-10px]">
              {/* Círculo de número con efecto de resplandor */}
              <div className="absolute -top-6 -left-6 z-20">
                <div className="absolute inset-0 bg-indigo-500/30 rounded-full blur-md animate-pulse"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-full flex items-center justify-center shadow-xl border-2 border-indigo-400/30">
                  <span className="text-3xl font-bold text-white">2</span>
                </div>
              </div>

              {/* Tarjeta principal */}
              <div className="relative bg-gradient-to-br from-indigo-900/40 to-violet-900/40 backdrop-blur-sm p-8 pt-10 rounded-xl border border-indigo-500/30 shadow-lg shadow-indigo-900/20 overflow-hidden h-full">
                {/* Fondo glow */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-indigo-500/10 rounded-full blur-xl"></div>

                {/* Icono */}
                <div className="bg-indigo-500/20 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Search className="w-8 h-8 text-indigo-400" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">
                  Selecciona el empleo
                </h3>

                <p className="text-gray-300 mb-6 relative z-10">
                  Pega el link o la descripción completa del empleo que deseas para una personalización óptima.
                </p>

                {/* Lista detallada */}
                <ul className="space-y-3 text-gray-300 relative z-10">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-indigo-400 mt-0.5 flex-shrink-0" />
                    <span>Análisis de requisitos y palabras clave del puesto</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-indigo-400 mt-0.5 flex-shrink-0" />
                    <span>Identificación de habilidades específicas requeridas</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="relative group md:transform md:transition-all md:duration-500 md:hover:translate-y-[-10px]">
              {/* Círculo de número con efecto de resplandor */}
              <div className="absolute -top-6 -left-6 z-20">
                <div className="absolute inset-0 bg-purple-500/30 rounded-full blur-md animate-pulse"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-800 rounded-full flex items-center justify-center shadow-xl border-2 border-purple-400/30">
                  <span className="text-3xl font-bold text-white">3</span>
                </div>
              </div>

              {/* Tarjeta principal */}
              <div className="relative bg-gradient-to-br from-purple-900/40 to-fuchsia-900/40 backdrop-blur-sm p-8 pt-10 rounded-xl border border-purple-500/30 shadow-lg shadow-purple-900/20 overflow-hidden h-full">
                {/* Fondo glow */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"></div>

                {/* Icono */}
                <div className="bg-purple-500/20 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Sparkles className="w-8 h-8 text-purple-400" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">
                  Obtén tu CV optimizado
                </h3>

                <p className="text-gray-300 mb-6 relative z-10">
                  Nuestra IA optimiza y adapta tu perfil al puesto deseado con un diseño profesional personalizado.
                </p>

                {/* Lista detallada */}
                <ul className="space-y-3 text-gray-300 relative z-10">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span>CV optimizado para superar filtros ATS automáticos</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span>Carta de presentación y análisis de mejoras incluidos</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA para empezar */}
          <div className="mt-16 text-center">
            <button
              onClick={onAuthClick}
              className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg transition-all transform hover:scale-105 hover:shadow-[0_0_30px_rgba(79,70,229,0.7)]"
            >
              <span className="relative z-10 flex items-center gap-2">
                <span>Empezar ahora</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg blur opacity-0 group-hover:opacity-50 transition-opacity" />
            </button>
          </div>
        </div>
      </section>

      {/* AI Analysis Feature */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-br from-gray-900 to-purple-950">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/3 right-1/3 w-[300px] h-[300px] bg-purple-500/10 rounded-full blur-[80px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full text-blue-400 mb-6 border border-blue-500/30">
              <Bot className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Análisis Inteligente</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Potencia tu CV con Inteligencia Artificial
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Nuestra IA analiza tu experiencia y optimiza tu CV para maximizar tus oportunidades laborales
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="group bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-blue-500/50 transition-all">
              <Zap className="w-10 h-10 text-yellow-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-bold text-white mb-2">Análisis Instantáneo</h3>
              <p className="text-gray-400">Análisis completo de tu experiencia en segundos</p>
            </div>

            <div className="group bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
              <Layout className="w-10 h-10 text-purple-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-bold text-white mb-2">Múltiples diseños con colores personalizables</h3>
              <p className="text-gray-400">Adaptados para cada profesión e industria</p>
            </div>

            <div className="group bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-green-500/50 transition-all">
              <Search className="w-10 h-10 text-green-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-bold text-white mb-2">Optimización ATS</h3>
              <p className="text-gray-400">Compatible con sistemas de seguimiento</p>
            </div>

            <div className="group bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-red-500/50 transition-all">
              <BarChart className="w-10 h-10 text-red-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-bold text-white mb-2">Análisis Detallado</h3>
              <p className="text-gray-400">Puntuación y recomendaciones de mejora</p>
            </div>
          </div>
        </div>
      </section>

      {/* CV Score Comparison */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-br from-gray-900 to-purple-950">
        <div className="absolute inset-0">
          <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] bg-purple-500/10 rounded-full blur-[80px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Puntuación promedio del CV
            </h2>
            <p className="text-xl text-gray-300">
              Nuestros CV obtienen un 9/10 en promedio vs 3.5/10 de CV tradicionales
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-green-500/30">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white font-bold">Curvia AI</span>
                  <span className="text-green-400 font-bold">9.0/10</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-4">
                  <div className="bg-gradient-to-r from-green-400 to-green-500 h-4 rounded-full" style={{ width: '90%' }}></div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-red-500/30">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white font-bold">CV Tradicional</span>
                  <span className="text-red-400 font-bold">3.5/10</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-4">
                  <div className="bg-gradient-to-r from-red-400 to-red-500 h-4 rounded-full" style={{ width: '35%' }}></div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-6">¿Por qué la diferencia?</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300">Optimización automática para cada puesto específico</p>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300">Análisis de palabras clave relevantes para la industria</p>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300">Formato profesional optimizado para ATS</p>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300">Enfoque en logros y competencias relevantes</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-br from-purple-950 to-gray-900">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Historias de Éxito
            </h2>
            <p className="text-xl text-gray-300">
              Descubre cómo CV Maker AI ha ayudado a otros profesionales
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-white/10 hover:border-blue-500/50 transition-all">
              <div className="flex items-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-400 group-hover:scale-110 transition-transform" fill="currentColor" />
                ))}
              </div>
              <p className="text-gray-300 mb-6">
                "El diseño de mi CV llamó la atención inmediatamente. El reclutador mencionó específicamente lo profesional que se veía durante la entrevista."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 overflow-hidden rounded-full">
                  <img
                    src="/profiles/mujer_1.jpg"
                    alt="María A."
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-white font-medium">María A.</p>
                  <p className="text-gray-400 text-sm">Diseñadora UX/UI</p>
                </div>
              </div>
            </div>

            <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-white/10 hover:border-blue-500/50 transition-all">
              <div className="flex items-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-400 group-hover:scale-110 transition-transform" fill="currentColor" />
                ))}
              </div>
              <p className="text-gray-300 mb-6">
                "Conseguí entrevistas en 3 empresas diferentes en la primera semana. La optimización para cada puesto específico hizo toda la diferencia."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 overflow-hidden rounded-full">
                  <img
                    src="/profiles/hombre_1.jpg"
                    alt="Juan R."
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-white font-medium">Juan R.</p>
                  <p className="text-gray-400 text-sm">Desarrollador Full Stack</p>
                </div>
              </div>
            </div>

            <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-white/10 hover:border-blue-500/50 transition-all">
              <div className="flex items-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-400 group-hover:scale-110 transition-transform" fill="currentColor" />
                ))}
              </div>
              <p className="text-gray-300 mb-6">
                "La función de análisis de ofertas laborales es increíble. Mi CV estaba perfectamente alineado con los requisitos del puesto."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 overflow-hidden rounded-full">
                  <img
                    src="/profiles/hombre_2.jpg"
                    alt="Carlos P."
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-white font-medium">Carlos P.</p>
                  <p className="text-gray-400 text-sm">Gerente de Proyecto</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job-specific Optimization */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-br from-gray-900 to-purple-950">
        <div className="absolute inset-0">
          <div className="absolute bottom-1/3 left-1/3 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full text-blue-400 mb-6 border border-blue-500/30">
              <Briefcase className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Optimización Inteligente</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              CV Personalizado para Cada Empleo
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Nuestra IA analiza la descripción del empleo y adapta tu CV automáticamente
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg blur-xl" />
              <div className="relative bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-white/10">
                <div className="flex items-center gap-3 mb-6">
                  <Search className="w-6 h-6 text-blue-400" />
                  <span className="text-white font-medium">Análisis de Oferta Laboral</span>
                </div>
                <div className="space-y-4">
                  <div className="h-3 bg-white/10 rounded-full w-full" />
                  <div className="h-3 bg-white/10 rounded-full w-4/5" />
                  <div className="h-3 bg-white/10 rounded-full w-3/4" />
                </div>
                <div className="mt-8">
                  <div className="flex items-center gap-2 text-green-400 mb-2">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-sm font-medium">Palabras clave identificadas</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {['React', 'TypeScript', 'UI/UX', 'Agile'].map((keyword) => (
                      <span
                        key={keyword}
                        className="px-3 py-1 bg-blue-500/20 rounded-full text-blue-400 text-sm border border-blue-500/30"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">
                  Adaptación Automática
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                    <p className="text-gray-300">Identifica y destaca las competencias relevantes para el puesto</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                    <p className="text-gray-300">Reorganiza la información según las prioridades del empleador</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                    <p className="text-gray-300">Incorpora palabras clave específicas de la industria</p>
                  </li>
                </ul>
              </div>

              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">
                  Resultados Comprobados
                </h3>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">90%</p>
                    <p className="text-gray-400 text-sm">Tasa de coincidencia con requisitos del puesto</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">6x</p>
                    <p className="text-gray-400 text-sm">Más llamadas a entrevistas que con CVs tradicionales</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">24h</p>
                    <p className="text-gray-400 text-sm">Respuesta promedio de los reclutadores</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-b from-gray-900 to-purple-950">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/4 w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[80px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[200px] h-[200px] bg-purple-500/10 rounded-full blur-[60px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Users className="w-6 h-6 text-blue-400" />
              <span className="text-xl text-gray-400">Más de 10,000 profesionales confían en nosotros</span>
            </div>

            <div className="flex flex-wrap justify-center gap-8">
              <div className="group flex items-center gap-2 px-6 py-3 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-yellow-500/50 transition-all">
                <Star className="w-5 h-5 text-yellow-400 group-hover:scale-110 transition-transform" />
                <span className="text-2xl font-bold text-white">98%</span>
                <span className="text-gray-400">Tasa de satisfacción</span>
              </div>

              <div className="group flex items-center gap-2 px-6 py-3 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-green-500/50 transition-all">
                <Target className="w-5 h-5 text-green-400 group-hover:scale-110 transition-transform" />
                <span className="text-2xl font-bold text-white">6x</span>
                <span className="text-gray-400">Más entrevistas</span>
              </div>

              <div className="group flex items-center gap-2 px-6 py-3 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-blue-500/50 transition-all">
                <Award className="w-5 h-5 text-blue-400 group-hover:scale-110 transition-transform" />
                <span className="text-2xl font-bold text-white">89%</span>
                <span className="text-gray-400">Tasa de contratación</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full py-20 relative overflow-hidden bg-gray-950">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gray-900/80 mix-blend-overlay" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-900/10 rounded-full blur-[100px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <div className="bg-gray-900/60 backdrop-blur-sm p-12 rounded-xl border border-gray-800/80 shadow-lg max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
              Consigue el trabajo que mereces
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              No dejes que otro candidato menos calificado obtenga el puesto.
              Optimiza tu CV ahora y destaca entre la competencia.
            </p>
            <button
              onClick={onAuthClick}
              className="group relative px-8 py-4 bg-gradient-to-r from-gray-800 to-purple-900 text-white rounded-lg transition-all transform hover:scale-105 hover:shadow-[0_0_30px_rgba(88,28,135,0.7)]"
            >
              <span className="relative z-10 flex items-center gap-2">
                <span>Crear mi CV optimizado</span>
                <Sparkles className="w-5 h-5 animate-pulse text-yellow-200" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-purple-900 rounded-lg blur opacity-0 group-hover:opacity-50 transition-opacity" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}